/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck comment
import { Controller, useFormContext } from "react-hook-form";
import { Box, Typography as T } from "@mui/material";

import { Input, Label, LabelError, RequiredStar } from "components/atoms";
import {
  getErrorTextWithMultipleValidateFunc,
  validateSeveral,
} from "helpers/validation";
import { Country } from "store/types";
import { palette } from "theme/palette";

type Props = {
  name: string;
  labelText: string;
  countryCode?: Country | null;
  required?: boolean;
  labelErrorText?: string;
  placeholder: string;
  validateFunctionObject?: {
    [key: string]: (value: string) => boolean | string;
  };
  autoComplete?: string;
};

const PhoneInputNumberBox = ({
  name,
  required,
  labelText,
  placeholder,
  labelErrorText,
  validateFunctionObject = {},
  countryCode = {
    country_id: "1",
    name: "United States",
  },
  autoComplete,
}: Props) => {
  const { control } = useFormContext();

  const validateFuncErrorText = validateFunctionObject?.errText;
  const sanitizeCountryCode = (value: string) =>
    value.replace(/\D/g, "").slice(0, 3);

  const sanitizePhoneDigits = (value: string) =>
    value.replace(/\D/g, "").slice(0, 10);

  // Browser autofill for `tel` lands the FULL phone (incl. country code) in
  // whichever field the autocomplete attribute is on, regardless of which
  // sub-field the user expects it in. This helper detects "looks like a
  // full international phone was just pasted/autofilled" by digit count
  // and splits it into the right pieces. Returns null for inputs that
  // don't look like an autofill (manual keystrokes, partial entries) so
  // the caller can fall back to its normal per-field sanitization.
  //
  // Rules:
  //   - 10 digits exactly → US/CA national format, leave country code alone.
  //   - 11+ digits → trailing 10 are the phone, leading digits are the
  //     country code (with or without a "+" — the regex strips it anyway).
  //   - <10 digits → not an autofill; let the caller handle.
  const splitAutofilledPhone = (
    rawValue: string,
    currentCountryCode: string
  ): { countryCode: string; phoneDigits: string } | null => {
    const digits = rawValue.replace(/\D/g, "");
    if (digits.length === 10) {
      return {
        countryCode: currentCountryCode,
        phoneDigits: digits,
      };
    }
    if (digits.length > 10) {
      return {
        countryCode: sanitizeCountryCode(digits.slice(0, digits.length - 10)),
        phoneDigits: digits.slice(-10),
      };
    }
    return null;
  };

  const formatPhoneDigits = (digits: string) => {
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const lineNumber = digits.slice(6, 10);

    return [area, prefix, lineNumber].filter(Boolean).join("-");
  };

  const parseCombinedValue = (combinedValue: string) => {
    if (!combinedValue) {
      const parsedCountryCode = sanitizeCountryCode(
        countryCode?.dial_code || "1"
      );

      return {
        countryCode: parsedCountryCode || "1",
        phoneDigits: "",
      };
    }

    const normalizedValue = combinedValue.replace(/^\+/, "");
    const [rawCountryCode = "", ...rest] = normalizedValue.split("-");
    const rawPhone = rest.join("-");

    const parsedCountryCode = sanitizeCountryCode(rawCountryCode);
    const parsedPhone = sanitizePhoneDigits(rawPhone);

    return {
      countryCode: parsedCountryCode,
      phoneDigits: parsedPhone,
    };
  };

  const buildCombinedValue = (
    countryCodeDigits: string,
    phoneDigits: string
  ) => {
    const formattedPhone = formatPhoneDigits(phoneDigits);

    return `+${countryCodeDigits}-${formattedPhone}`;
  };

  const validateFunctions = [
    (v: string) =>
      required ? Boolean(parseCombinedValue(v).phoneDigits) : true,
    (v: string) => {
      const { phoneDigits } = parseCombinedValue(v);

      if (!required && !phoneDigits) return true;

      return phoneDigits.length === 10;
    },
  ];

  if (validateFunctionObject?.func)
    validateFunctions.push(validateFunctionObject.func);

  const rulesObject = {
    validate: (outV: string) => validateSeveral(outV, validateFunctions),
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue=""
      rules={rulesObject}
      render={({
        field: { onChange, value, ref },
        fieldState: { invalid },
      }) => (
        <>
          <Label
            sx={{
              marginLeft: "2px",
            }}
          >
            {required && <RequiredStar />}
            <T variant="inputTitle">{labelText}</T>
          </Label>
          <Box
            sx={{
              width: "100%",
              position: "relative",
            }}
          >
            {(() => {
              const { countryCode: parsedCountryCode, phoneDigits } =
                parseCombinedValue(value);
              const phoneValue = formatPhoneDigits(phoneDigits);

              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    height: 60,
                    border: `1px solid ${invalid ? palette.error : palette.grey}`,
                    borderRadius: "10px",
                    paddingLeft: "22px",
                    fontSize: "16px",
                    fontWeight: 400,
                    color: palette.black,
                    lineHeight: "18.4px",
                    transition: "border-color 0.2s ease",
                    "&:focus-within": {
                      borderColor: palette.accent,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      paddingRight: 1,
                    }}
                  >
                    <Input
                      value={`+${parsedCountryCode}`}
                      onChange={(e) => {
                        const split = splitAutofilledPhone(
                          e.target.value,
                          parsedCountryCode
                        );
                        if (split) {
                          onChange(
                            buildCombinedValue(
                              split.countryCode,
                              split.phoneDigits
                            )
                          );
                          return;
                        }
                        // Manual country-code entry — keep existing phone.
                        onChange(
                          buildCombinedValue(
                            sanitizeCountryCode(e.target.value),
                            phoneDigits
                          )
                        );
                      }}
                      placeholder="+1"
                      error={invalid}
                      inputProps={{
                        inputMode: "tel",
                        // No autoComplete here — `tel` autofill fills the
                        // FULL international number, which would land in
                        // this tiny country-code field as e.g.
                        // "+12485829210". The phone-number input below
                        // owns autoComplete and uses splitAutofilledPhone
                        // to peel the country-code off into this field
                        // when the autofilled value is 11+ digits.
                        autoComplete: "off",
                        // No maxLength — if a paste/autofill ever lands
                        // here directly, splitAutofilledPhone needs the
                        // full string (>=10 digits) to detect and split.
                        // Display stays bounded by sanitizeCountryCode
                        // (max 3 digits).
                      }}
                      sx={{
                        border: "none !important",
                        backgroundColor: "transparent",
                        height: "100%",
                        borderRadius: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        textAlign: "right",
                        "&.Mui-focused": {
                          border: "none",
                        },
                        "&.Mui-error": {
                          border: "none",
                        },
                        "> input": {
                          textAlign: "right",
                          maxWidth: "40px",
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      height: "100%",
                    }}
                  >
                    <Input
                      value={phoneValue}
                      onChange={(e) => {
                        const split = splitAutofilledPhone(
                          e.target.value,
                          parsedCountryCode
                        );
                        if (split) {
                          onChange(
                            buildCombinedValue(
                              split.countryCode,
                              split.phoneDigits
                            )
                          );
                          return;
                        }
                        // Manual phone entry — keep existing country code.
                        onChange(
                          buildCombinedValue(
                            parsedCountryCode,
                            sanitizePhoneDigits(e.target.value)
                          )
                        );
                      }}
                      placeholder={placeholder || "999-999-9999"}
                      error={invalid}
                      inputRef={ref}
                      inputProps={{
                        inputMode: "tel",
                        // autoComplete moves here from the country-code
                        // input above. Browsers fill `tel` with the full
                        // international number; the onChange handler runs
                        // splitAutofilledPhone which peels the leading
                        // country-code digits off when the value has
                        // 11+ digits.
                        autoComplete,
                      }}
                      sx={{
                        border: "none !important",
                        backgroundColor: "transparent",
                        height: "100%",
                        borderRadius: 0,
                        paddingLeft: 0,
                        width: "100%",
                        "&.Mui-focused": {
                          border: "none",
                        },
                        "&.Mui-error": {
                          border: "none",
                        },
                      }}
                    />
                  </Box>
                </Box>
              );
            })()}
          </Box>
          <Box>
            {invalid && (
              <LabelError
                labelErrorText={
                  labelErrorText ||
                  getErrorTextWithMultipleValidateFunc(value, {
                    func: validateFunctionObject?.func,
                    errText: validateFuncErrorText,
                  })
                }
              />
            )}
          </Box>
        </>
      )}
    />
  );
};

export default PhoneInputNumberBox;
